import { list } from '@/router/utils'
import { AttachmentTableWithTabs } from '@/shared/components/attachments'
import { Pill } from '@/shared/components/pill'
import { attachmentApi } from '@/store/services/attachment.service'
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader } from '@material-tailwind/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Link,
  createSearchParams,
  generatePath,
  useLoaderData,
  useSearchParams
} from 'react-router-dom'
import { useAuthorization } from '@/hooks'
import { store } from '@/store'

export const AssetV2AttachmentList = () => {
  const { t } = useTranslation()
  const { hasAccess } = useAuthorization({
    redirection: { enable: true }
  })
  const asset = useLoaderData()
  const [searchParams] = useSearchParams()
  const [isNavigatingToUpload, setIsNavigatingToUpload] = useState(false)
  const [showUploadLoadingMessage, setShowUploadLoadingMessage] = useState(false)
  const modalSearchParams = useMemo(() => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('attachment_tab')
    nextParams.delete('attachment_reload')
    return nextParams
  }, [searchParams])
  const currentUrlParams = modalSearchParams.toString()
    ? `?${modalSearchParams.toString()}`
    : ''

  const defaultSearchParams = useMemo(() => {
    const reloadToken = searchParams.get('attachment_reload')

    return {
      attachable_type: 'Asset',
      attachable_id: asset.id,
      ...(reloadToken ? { attachment_reload: reloadToken } : {})
    }
  }, [asset.id, searchParams])
  const pillOptions = useMemo(() => {
    return [
      {
        label: asset.asset_type?.identifier_label || 'Identificador',
        value: asset.identifier_value
      },
      {
        label: t('contract.one'),
        value: `${asset.contractor_contract?.id || '-'} - ${asset.contractor_contract?.name || '-'}`
      },
      {
        label: t('contractor.one'),
        value: asset.contractor_contract?.company?.name
      }
    ]
  }, [asset, t])

  const getMore = async (searchParams) => {
    const { attachments = [], meta = {} } = await store.dispatch(
      attachmentApi.endpoints.getAttachments.initiate(
        `?${searchParams.toString()}`,
        { forceRefetch: true }
      )
    ).unwrap()

    const currentPage = Number(searchParams.get('page'))
    const hasMore = currentPage < (meta?.pagination?.total_pages || 0)

    return { attachments, hasMore }
  }

  useEffect(() => {
    hasAccess('asset_v2#attachments')
  }, [])

  useEffect(() => {
    if (!isNavigatingToUpload) {
      setShowUploadLoadingMessage(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowUploadLoadingMessage(true)
    }, 500)

    return () => window.clearTimeout(timeoutId)
  }, [isNavigatingToUpload])

  const blockWhileNavigating = (event) => {
    if (!isNavigatingToUpload) return

    event.preventDefault()
  }

  return (
    <Dialog size='xl' className='h-4/5' open={true}>
      <DialogHeader>Detalle de documentos subidos</DialogHeader>
      <DialogBody className='relative overflow-hidden' style={{ height: 'inherit' }}>
        {isNavigatingToUpload && (
          <div className='absolute inset-0 z-10 bg-white/55 pointer-events-auto'>
            {showUploadLoadingMessage && (
              <div className='absolute inset-0 flex items-center justify-center'>
                <div className='rounded-lg bg-white px-4 py-3 shadow-md'>Cargando...</div>
              </div>
            )}
          </div>
        )}
        <div className='flex gap-4 mb-5'>
          {pillOptions.map((pillOption) => (
            <Pill key={pillOption.label} {...pillOption} />
          ))}
        </div>

        <AttachmentTableWithTabs
          initialSearchParams={defaultSearchParams}
          getMore={getMore}
          initialTab={searchParams.get('attachment_tab')}
          uploadPermission='asset_v2#upload_attachments'
          onUploadNavigateStart={() => setIsNavigatingToUpload(true)}
          buildUploadPath={(attachment, currentTab) =>
            generatePath(
              '/assets_v2/:id/attachments/:attachmentId/upload_attachment',
              {
                id: asset.id,
                attachmentId: attachment.id
              }
            ) + `?${createSearchParams({
              ...Object.fromEntries(searchParams.entries()),
              attachment_tab: currentTab
            }).toString()}`
          }
        />
      </DialogBody>
      <DialogFooter className='flex gap-3'>
        <Link to={`${list('assets_v2')}${currentUrlParams}`} onClick={blockWhileNavigating}>
          <Button variant='outlined' color='white' disabled={isNavigatingToUpload}>
            Volver
          </Button>
        </Link>
      </DialogFooter>
    </Dialog>
  )
}
